import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  cancelDocumentCondition,
  DocumentCondition,
  listDocumentConditions,
  reviewDocumentCondition,
} from '../services/MendsVisionService';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

/** Dia e mês (dd/MM) no fuso do navegador. */
export function formatDay(iso: string): string {
  return format(iso, false);
}

/** Dia, mês e hora (dd/MM HH:mm) no fuso do navegador. */
export function formatDayTime(iso: string): string {
  return format(iso, true);
}

function format(iso: string, withTime: boolean): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const parts = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const day = `${get('day')}/${get('month')}`;
  return withTime ? `${day} ${get('hour')}:${get('minute')}` : day;
}

const CHANNEL_LABEL: Record<string, string> = { whatsapp: 'WhatsApp', email: 'e-mail', human: 'vendedor' };

function pillTone(state: string): string {
  if (state === 'NeedsReview' || state === 'Overdue') return 'mai-pill mai-pill-high';
  if (state === 'Waiting' || state === 'AwaitingSend') return 'mai-pill mai-pill-wait';
  if (state === 'Sent' || state === 'Received') return 'mai-pill mai-pill-ai';
  return 'mai-pill';
}

/**
 * Documentos que o cliente prometeu enviar (REG-24 / REG-35): o Registrar acompanha o WhatsApp e o e-mail e, quando
 * chega um arquivo, a IA confere se é o documento esperado. Na dúvida, o vendedor decide aqui. Nada é dado como recebido
 * só pela promessa; a confirmação ao cliente só sai com o clique de envio do Registrar.
 */
export const DocumentConditionsList: React.FC<{ sectorId: number; contactId: number }> = ({ sectorId, contactId }) => {
  const [items, setItems] = useState<DocumentCondition[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const controller = useRef<AbortController | null>(null);

  const load = useCallback(() => {
    controller.current?.abort();
    const c = new AbortController();
    controller.current = c;
    return listDocumentConditions(sectorId, contactId, c.signal)
      .then((list) => { if (!c.signal.aborted) setItems(list); })
      .catch(() => { if (!c.signal.aborted) setItems([]); });
  }, [sectorId, contactId]);

  useEffect(() => {
    if (!sectorId || !contactId) return undefined;
    void load();
    return () => controller.current?.abort();
  }, [sectorId, contactId, load]);

  async function run(id: string, call: () => Promise<unknown>) {
    setBusy(id);
    setError(null);
    try {
      await call();
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, 'Não foi possível concluir. Tente de novo.'));
    } finally {
      setBusy(null);
    }
  }

  if (items.length === 0) return null;

  return (
    <div className="mai-doc-conditions" style={{ marginTop: 12 }}>
      <div className="mai-section-label">Documentos aguardados</div>
      {error && <div className="mai-error" role="alert">{error}</div>}
      {items.map((c) => {
        const ev = c.evidence;
        const disabled = busy === c.id;
        return (
          <div key={c.id} className="mai-card" style={{ marginTop: 8 }}>
            <div className="mai-row" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong>{c.documentLabel}</strong>
              <span className={pillTone(c.state)}>{c.stateLabel}</span>
            </div>
            <div className="mai-muted">{c.conditionText}</div>
            {c.dueAt && <div className="mai-muted">{`Prometido para ${formatDay(c.dueAt)}`}</div>}
            {ev && (
              <div className="mai-muted">
                <div>
                  {`Recebido pelo ${CHANNEL_LABEL[ev.source] ?? ev.source} em ${formatDayTime(ev.receivedAtUtc)}`
                    + (ev.fileName ? ` · ${ev.fileName}` : '')}
                </div>
                {ev.summary && <div>{ev.summary}</div>}
              </div>
            )}
            {(c.canReview || c.canCancel) && (
              <div className="mai-btn-row" style={{ marginTop: 6 }}>
                {c.canReview && (
                  <>
                    <button
                      type="button"
                      className="mai-btn mai-btn-soft"
                      disabled={disabled}
                      onClick={() => run(c.id, () => reviewDocumentCondition(sectorId, c.id, true))}
                    >
                      É este documento
                    </button>
                    <button
                      type="button"
                      className="mai-btn mai-btn-ghost"
                      disabled={disabled}
                      onClick={() => run(c.id, () => reviewDocumentCondition(sectorId, c.id, false))}
                    >
                      Não é
                    </button>
                  </>
                )}
                {c.canCancel && (
                  <button
                    type="button"
                    className="mai-btn mai-btn-ghost"
                    disabled={disabled}
                    onClick={() => run(c.id, () => cancelDocumentCondition(sectorId, c.id))}
                  >
                    Parar de acompanhar
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
